import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import ProductPageProductInformation from './ProductPageProductInformation';
import StockStatus from '../StockStatus/StockStatus';
import ProductPageUsps from './ProductPageUsps';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { ProductPageModel } from '../../Types/ProductPageModel';
import MeasuredWallpaperCalculator, {
  isValidHeight,
  isValidWidth,
} from './MeasuredWallpaperCalculator';
import ProductPageImageGallery from './ProductPageImageGallery';
import MeasuredWallpaperBuySection from './MeasuredWallpaperBuySection';
import MeasuredWallpaperCuttingsData from '../../Types/MeasuredWallpaperCuttingsData';
import clsx from 'clsx';
import useYotpoBottomLine from '../../Hooks/useYotpoBottomLine';
import YotpoUgc from '../Yotpo/YotpoUgc';
import ProductPageTabsContainer from '../ProductPageTabs/ProductPageTabsContainer';
import ProductPageStructuredData from './ProductPageStructuredData';
import ProductRelationBlockContainer from './ProductRelationBlockContainer';
import ProductPageLinkBlock from './ProductPageLinkBlock';
import { translate } from '../../Services/translation';
import ProductPageBuyGuideContainer from './ProductPageBuyGuideContainer';
import ProductSelector from './ProductSelector';
import { setActiveProduct } from '../../Reducers/ProductPage/Actions/setActiveProduct.action';
import { useTypedDispatch } from '../../Hooks/useTypedDispatch';
import { trackingServiceProductPageViewEvent } from '../../Services/Tracking.service';
import ProductPageMobileImages from './ProductPageMobileImages';
import MeasuredWallpaperCutout from '../MeasuredWallpaperCutout/MeasuredWallpaperCutout';
import {
  PRICE_TOGGLE_BEFORE_RELOAD_EVENT,
  StockStatusLevel,
} from '../../constants';
import CustomWallpaperDropzone, {
  CustomWallpaperFile,
} from '../MeasuredWallpaperCutout/CustomWallpaper/CustomWallpaperDropzone';
import ProductPageRoomvoButton from './ProductPageRoomvoButton';
import DiscontinuedItem from '../DiscontinuedItem/DiscontinuedItem';
import ProductPageMaterialSelector from './ProductPageMaterialSelector';

const MIN_KVCM_SIZE = 30000;

export const getCutoutUrl = () => {};

const MeasuredWallpaperProductPage = (model: ProductPageModel) => {
  const dispatch = useTypedDispatch();
  const { currentProduct: productFromState, loadingProductId } =
    useTypedSelector((state) => state.productPage);
  const { currency } = useTypedSelector((state) => state.cart);
  const currentProduct = useMemo(
    () => productFromState ?? model,
    [productFromState, model]
  );
  const yotpoBottomLine = useYotpoBottomLine(
    currentProduct.productItem.id
  );

  const [calculatorActive, setCalculatorActive] = useState(false);
  const [calculatorHeight, setCalculatorHeight] = useState(0);
  const [calculatorWidth, setCalculatorWidth] = useState(0);
  const [wallpaperMaterial, setWallpaperMaterial] = useState<string>('');
  const [initialCuttingData, setInitialCuttingData] = useState<
    { cutFromX: number; cutFromY: number; zoom: number } | undefined
  >();
  const [flipHorizontally, setFlipHorizontally] = useState(false);
  const [customWallpaperFile, setCustomWallpaperFile] = useState<
    CustomWallpaperFile | undefined
  >();
  const [cuttingsData, setCuttingsData] =
    useState<MeasuredWallpaperCuttingsData>(
      {} as MeasuredWallpaperCuttingsData
    );

  const measuredWallpaperData = useMemo(
    () =>
      currentProduct.measuredWallpaper
        ? {
            ...currentProduct.measuredWallpaper,
            ...(customWallpaperFile
              ? {
                  patternWidth: customWallpaperFile.width,
                  patternHeight: customWallpaperFile.height,
                  imageUrl: customWallpaperFile.getUrl(),
                }
              : {}),
          }
        : undefined,
    [currentProduct.measuredWallpaper, customWallpaperFile]
  );

  const calculatedSizeTooSmall = useMemo(
    () => calculatorWidth * calculatorHeight < MIN_KVCM_SIZE,
    [calculatorHeight, calculatorWidth]
  );

  useEffect(
    () => setCalculatorActive(calculatorWidth > 0 && calculatorHeight > 0),
    [calculatorWidth, calculatorHeight]
  );

  useLayoutEffect(() => {
    if (calculatorActive) {
      window.scrollTo({
        top: document.getElementById('measured-wallpaper-cutout')
          ?.offsetTop,
        behavior: 'smooth',
      });
    }
  }, [calculatorActive, calculatorWidth, calculatorHeight]);

  useEffect(() => {
    trackingServiceProductPageViewEvent(currency);
  }, []);

  useEffect(() => {
    if (!measuredWallpaperData) return;

    const searchParams = new URLSearchParams(window.location.search);
    const wallWidthFromUrl = searchParams.get('w');
    const wallHeightFromUrl = searchParams.get('h');
    const xPos = searchParams.get('x');
    const yPos = searchParams.get('y');
    const zoom = searchParams.get('zoom');
    const mirrored = searchParams.get('mirrored');

    const numericX = parseFloat(xPos ?? 'NaN');
    const numericY = parseFloat(yPos ?? 'NaN');
    const numericZoom = parseFloat(zoom ?? 'NaN');

    if (
      !!xPos &&
      !!yPos &&
      !Number.isNaN(numericX) &&
      !Number.isNaN(numericY)
    ) {
      setInitialCuttingData({
        cutFromX: numericX,
        cutFromY: numericY,
        zoom: !!zoom && !Number.isNaN(numericZoom) ? numericZoom : 0,
      });
    }

    if (mirrored?.toLowerCase() == 'true') {
      setFlipHorizontally(true);
    }

    const numericWidth = parseInt(wallWidthFromUrl ?? 'NaN');
    const numericHeight = parseInt(wallHeightFromUrl ?? 'NaN');

    if (
      !!wallHeightFromUrl &&
      !!wallWidthFromUrl &&
      !Number.isNaN(numericWidth) &&
      !Number.isNaN(numericHeight) &&
      isValidWidth(measuredWallpaperData, numericWidth) &&
      isValidHeight(measuredWallpaperData, numericHeight)
    ) {
      setCalculatorWidth(numericWidth);
      setCalculatorHeight(numericHeight);
      setCalculatorActive(false);
    }
  }, []);

  useEffect(() => {
    if (!calculatorActive) return;

    const onPriceToggleBeforeReload = () => {
      const url = new URL(window.location.href);
      url.searchParams.set('x', cuttingsData.cutFromX.toString());
      url.searchParams.set('y', cuttingsData.cutFromY.toString());
      url.searchParams.set('w', calculatorWidth.toString());
      url.searchParams.set('h', calculatorHeight.toString());
      url.searchParams.set('zoom', cuttingsData.zoom.toString());
      url.searchParams.set('mirrored', cuttingsData.mirrored.toString());

      window.history.replaceState(
        PRICE_TOGGLE_BEFORE_RELOAD_EVENT,
        '',
        url.toString()
      );
    };

    window.addEventListener(
      PRICE_TOGGLE_BEFORE_RELOAD_EVENT,
      onPriceToggleBeforeReload
    );
    return () => {
      window.removeEventListener(
        PRICE_TOGGLE_BEFORE_RELOAD_EVENT,
        onPriceToggleBeforeReload
      );
    };
  }, [calculatorActive, calculatorHeight, calculatorWidth, cuttingsData]);

  const onCustomWallpaperSelected = (file: CustomWallpaperFile) => {
    setCustomWallpaperFile(file);
  };

  return (
    <div itemScope itemType="http://schema.org/Product">
      {!calculatorActive && (
        <ProductPageMobileImages
          images={currentProduct.productItem.images}
          videoUrl={currentProduct.productItem.videoUrl}
        />
      )}
      <div className="lg:container mb-8 grid grid-cols-1 lg:grid-cols-3">
        {calculatorActive && measuredWallpaperData && (
          <MeasuredWallpaperCutout
            wallWidth={calculatorWidth}
            wallHeight={calculatorHeight}
            initialCutFromX={initialCuttingData?.cutFromX}
            initialCutFromY={initialCuttingData?.cutFromY}
            initialZoom={initialCuttingData?.zoom}
            measuredWallpaper={measuredWallpaperData}
            onCuttingsDataChange={setCuttingsData}
            flipHorizontally={flipHorizontally}
            onFlipHorizontally={setFlipHorizontally}
          />
        )}
        {!calculatorActive && (
          <div className="relative grid lg:col-span-2">
            <div className="col-span-2 hidden gap-4 lg:grid">
              <ProductPageImageGallery
                images={currentProduct.productItem.images}
                videoUrl={currentProduct.productItem.videoUrl}
              />
              {currentProduct.productItem.id && (
                <div className="lg:block">
                  <YotpoUgc productId={currentProduct.productItem.id} />
                </div>
              )}
            </div>
          </div>
        )}
        <div className="px-4 lg:-mt-8 lg:flex lg:justify-center lg:px-4 xl:px-8 md-down:mx-auto">
          <div className="w-full self-start pt-6 lg:sticky lg:top-0 lg:max-w-[568px] lg:pt-8">
            <ProductPageProductInformation
              title={currentProduct.productItem.name}
              bottomLine={yotpoBottomLine}
              product={currentProduct.productItem}
              muralCanvasDisclaimer={
                currentProduct.stockStatus?.level !==
                  StockStatusLevel.Inactive &&
                (!measuredWallpaperData?.isCustomWallpaper ||
                  !!measuredWallpaperData.imageUrl)
                  ? translate('product-page.mural-canvas.disclaimer')
                  : undefined
              }
              articleNumber={currentProduct.productItem.id}
            />
            {currentProduct.stockStatus && (
              <StockStatus {...currentProduct.stockStatus} />
            )}
            {currentProduct.productItem.itemStatus && (
              <DiscontinuedItem
                itemStatus={currentProduct.productItem.itemStatus}
              />
            )}
            {measuredWallpaperData &&
              measuredWallpaperData.patternHeight > 0 &&
              measuredWallpaperData.patternWidth > 0 &&
              measuredWallpaperData.breadthWidth > 0 &&
              currentProduct?.stockStatus?.level !==
                StockStatusLevel.Inactive && (
                <MeasuredWallpaperCalculator
                  calculatorActive={calculatorActive}
                  calculatedSizeTooSmall={calculatedSizeTooSmall}
                  setCalculatorHeight={setCalculatorHeight}
                  setCalculatorWidth={setCalculatorWidth}
                  calculatorWidth={calculatorWidth}
                  calculatorHeight={calculatorHeight}
                  measuredWallpaper={measuredWallpaperData}
                  disableWallSizeSelection={
                    (typeof initialCuttingData?.cutFromX !== 'undefined' &&
                      typeof initialCuttingData?.cutFromY !==
                        'undefined') ||
                    (measuredWallpaperData.isCustomWallpaper &&
                      !customWallpaperFile)
                  }
                />
              )}
            {!measuredWallpaperData?.isPreview &&
              measuredWallpaperData?.isCustomWallpaper && (
                <div className="mb-4">
                  <CustomWallpaperDropzone
                    onFileSelected={onCustomWallpaperSelected}
                    selectedImageUrl={
                      calculatorActive
                        ? undefined
                        : customWallpaperFile?.getUrl()
                    }
                    isReplacementZone={calculatorActive}
                    customWallpaperInfo={
                      measuredWallpaperData?.customWallpaperInfo!
                    }
                  />
                </div>
              )}
            {currentProduct.materials?.value?.length > 0 && (
              <ProductPageMaterialSelector
                currentProduct={currentProduct.productItem}
                materials={currentProduct.materials.value}
                currentMaterial={wallpaperMaterial}
                setCurrentMaterial={setWallpaperMaterial}
                modalContent={model.materialInfo}
              />
            )}
            <MeasuredWallpaperBuySection
              width={calculatorWidth}
              height={calculatorHeight}
              wallpaperMaterial={wallpaperMaterial}
              priceModel={currentProduct.productItem.price}
              articleNumber={currentProduct.productItem.id ?? ''}
              calculatedSizeTooSmall={calculatedSizeTooSmall}
              cuttingsData={cuttingsData}
              samples={currentProduct.productItem.samples}
              stockstatus={currentProduct.stockStatus}
              customWallpaperFile={customWallpaperFile}
              disableBuyOption={currentProduct.disableBuyOption}
              buyButtonDisabled={false}
              buyButtonHidden={
                measuredWallpaperData?.isCustomWallpaper &&
                !calculatorActive
              }
              hasMaterialSelector={
                !!currentProduct.materials?.value?.length
              }
            />
            {(currentProduct.buyGuideBlocks != null ||
              (!model.hideRoomvoButton &&
                currentProduct.productItem.simulatorCompatible)) && (
              <div
                className="roomvo-container flex items-center justify-between"
                data-product-type-str="wall"
              >
                {model.buyGuideBlocks != null && (
                  <ProductPageBuyGuideContainer />
                )}
                {!model.hideRoomvoButton &&
                  currentProduct.productItem.simulatorCompatible && (
                    <ProductPageRoomvoButton
                      id={currentProduct.productItem.id}
                    />
                  )}
              </div>
            )}
            <ProductPageUsps uspBlock={currentProduct.uspBlock} />
            {currentProduct.variants?.length > 1 && (
              <ProductSelector
                items={currentProduct.variants}
                onVariantSelected={(id) => {
                  dispatch(
                    setActiveProduct({
                      id,
                      currentProductId: currentProduct.productItem.id,
                    })
                  );
                  setCalculatorWidth(0);
                  setCalculatorHeight(0);
                }}
                loadingProductId={loadingProductId}
                selectedProductId={currentProduct.productItem.id}
              />
            )}
          </div>
        </div>
      </div>
      <ProductPageTabsContainer productPage={currentProduct} />
      {currentProduct.productItem.id && (
        <div className="container lg:hidden">
          <YotpoUgc productId={currentProduct.productItem.id} />
        </div>
      )}
      <div className="container mb-16 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {currentProduct.arApplicationUrl && (
          <ProductPageLinkBlock
            className="lg:hidden"
            url={currentProduct.arApplicationUrl}
            preTitle={translate('product-page.ar-application')}
            title={translate('product-page.ar-application.title')}
            description={translate(
              'product-page.ar-application.description'
            )}
            buttonText={translate('product-page.ar-application.button')}
            iconName="cube-focus"
            openInNewWindow={true}
          />
        )}
        {!model.hideRoomvoButton && (
          <ProductPageLinkBlock
            preTitle={translate('product-page.wallpaper-simulator')}
            title={translate('product-page.wallpaper-simulator.title')}
            description={translate(
              'product-page.wallpaper-simulator.description'
            )}
            buttonText={translate(
              'product-page.wallpaper-simulator.button'
            )}
            iconName="images"
            displayArButton={true}
            displayButton={false}
            productId={currentProduct.productItem.id}
          />
        )}
      </div>
      <div className="container mb-16">
        <ProductRelationBlockContainer
          productRelations={[
            currentProduct.accessoriesProducts,
            currentProduct.similarProducts,
            currentProduct.matchingProducts,
          ]}
        />
      </div>
      <ProductPageStructuredData productPage={currentProduct} />
    </div>
  );
};

export default MeasuredWallpaperProductPage;
